import React, { useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import PostCommunityCard from "../components/cards/PostCommunityCard";
import {
  useCreateCommentMutation,
  useLazyGetPostQuery,
  useLikePostMutation,
  useUnlikePostMutation,
  useDeletePostMutation,
} from "../services/users/community";
import {
  useAdminLikeEmployeePostMutation,
  useAdminUnlikeEmployeePostMutation,
  useAdminCommentOnEmployeePostMutation,
  useAdminLikeEmployeeCommentMutation,
  useAdminUnlikeEmployeeCommentMutation,
} from "../services/organization/adminUsers";
import CreatePostModal from "../components/Modals/CreatePostModal";
import ConfirmationModal from "../components/Modals/ConfirmationModal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

export default function CommunityFeed({
  listPosts,
  posts,
  interactionMode = "employee",
  skipInitialFetch = false,
}) {
  const [allPosts, setAllPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [postId, setPostId] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const isOrgFeed = interactionMode === "organization";

  const [getPost] = useLazyGetPostQuery();
  const [addComment] = useCreateCommentMutation();
  const [like] = useLikePostMutation();
  const [unlike] = useUnlikePostMutation();
  const [deletePost] = useDeletePostMutation();

  const [adminLikePost] = useAdminLikeEmployeePostMutation();
  const [adminUnlikePost] = useAdminUnlikeEmployeePostMutation();
  const [adminAddComment] = useAdminCommentOnEmployeePostMutation();
  const [adminLikeComment] = useAdminLikeEmployeeCommentMutation();
  const [adminUnlikeComment] = useAdminUnlikeEmployeeCommentMutation();

  const { t } = useTranslation(["common"]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    if (skipInitialFetch && page === 1) return;
    listPosts(page);
  }, [page, skipInitialFetch, listPosts]);

  useEffect(() => {
    if (posts === undefined) return;
    const rows = posts.data ?? [];
    if (posts.pagination?.current === 1) {
      setAllPosts(rows);
    } else {
      setAllPosts((prev) => [...prev, ...rows]);
    }
  }, [posts]);

  const refreshOrgFeedFirstPage = async () => {
    await listPosts(1);
    setPage(1);
  };

  const handleToggleLike = async ({ postId: pid, isLikedByMe }) => {
    try {
      if (isOrgFeed) {
        const postRow = allPosts.find((p) => p.id === pid);
        const userId = postRow?.user?.id;
        if (!userId) return;
        if (isLikedByMe) {
          await adminUnlikePost({ userId, postId: pid }).unwrap();
        } else {
          await adminLikePost({ userId, postId: pid }).unwrap();
        }
        await refreshOrgFeedFirstPage();
      } else {
        let result;
        if (isLikedByMe) {
          result = await unlike(pid);
        } else {
          result = await like(pid);
        }
        if (!result?.error) await updateSinglePost(pid);
      }
    } catch (e) {
      toast.error(e?.data?.message || t("common:somethingWentWrong"));
    }
  };

  const handleSubmittingComment = async ({
    postId: pid,
    comment,
    setComment,
  }) => {
    if (!comment?.trim()) return;
    try {
      if (isOrgFeed) {
        const postRow = allPosts.find((p) => p.id === pid);
        const userId = postRow?.user?.id;
        if (!userId) return;
        await adminAddComment({
          userId,
          postId: pid,
          body: { body: comment },
        }).unwrap();
        setComment("");
        await refreshOrgFeedFirstPage();
      } else {
        const dataForm = new FormData();
        dataForm.append("body", comment);
        const result = await addComment({ postId: pid, body: dataForm });
        if (!result?.error) {
          await updateSinglePost(pid);
          setComment("");
        }
      }
    } catch (e) {
      toast.error(e?.data?.message || t("common:somethingWentWrong"));
    }
  };

  const handleToggleCommentLike = async (comment, postRow) => {
    try {
      const userId = postRow?.user?.id;
      if (!userId) return;
      if (comment.is_liked_by_me) {
        await adminUnlikeComment({
          commentId: comment.id,
          userId,
        }).unwrap();
      } else {
        await adminLikeComment({
          commentId: comment.id,
          userId,
        }).unwrap();
      }
      await refreshOrgFeedFirstPage();
    } catch (e) {
      toast.error(e?.data?.message || t("common:somethingWentWrong"));
    }
  };

  const updateSinglePost = async (pid) => {
    const post = await getPost(pid);
    setAllPosts((prevPosts) =>
      prevPosts.map((prevPost) => {
        if (prevPost?.id != pid) return prevPost;
        return post?.data?.data;
      })
    );
  };

  const handleDeletingPost = async (pid) => {
    const result = await deletePost(pid);
    if (!result.error) await listPosts(1);
  };

  return (
    <>
      {!isOrgFeed && (
        <CreatePostModal listPosts={listPosts} isVerticallyCentered />
      )}
      <ul className="">
        <InfiniteScroll
          className="feed-list"
          dataLength={allPosts.length}
          next={() => setPage(posts?.pagination?.next)}
          hasMore={posts?.pagination?.next}
          loader={<h4>{t("Loading")}</h4>}
          endMessage={
            <p className="font-color-grey" style={{ textAlign: "center" }}>
              {t("communityFeed:successfulPostsDisplay")}
            </p>
          }
          style={{
            overflow: "unset",
          }}
        >
          {allPosts.map((post) => {
            return (
              <li className="mb-3" key={post.id}>
                <PostCommunityCard
                  post={post}
                  handleToggleLike={handleToggleLike}
                  handleSubmittingComment={handleSubmittingComment}
                  setPostId={setPostId}
                  containerClassName="post-community-card--employee"
                  profileBasePath={
                    isOrgFeed ? "/admin/employee" : "/employee/profile"
                  }
                  onToggleCommentLike={
                    isOrgFeed ? handleToggleCommentLike : undefined
                  }
                />
              </li>
            );
          })}
        </InfiniteScroll>
      </ul>
      {!isOrgFeed && (
        <ConfirmationModal
          confirmationMessage={t("communityFeed:confirmPostDelete")}
          handleConfirmation={async () => {
            await handleDeletingPost(postId);
            setIsOpen(false);
          }}
          isOpened={isOpen}
          onClose={() => setIsOpen((status) => !status)}
        />
      )}
    </>
  );
}
